import { rpc } from '../lib/Rpc'
import { store } from '../store'
import { localDateString } from '../lib/utilsCommon'
import { loggerNamespace } from '../lib/Logger'
import { IGetAppointmentByFilterV2 } from '../interfaces/rpc/IGetAppointmentByFilterV2'
import { IAppointmentGetAppointment, IAppointmentContext } from '../interfaces/rpc/IAppointmentGetAppointment'
import { IAppointmentUpdateAppointment } from '../interfaces/rpc/IAppointmentUpdateAppointment'
import { IAppointmentSwapDates } from '../interfaces/rpc/IAppointmentSwapDates'
import { profileService } from './ProfileService'
import { t } from '../config/i18n'

const MAX_AGGREGATED_RANGE_DAYS = 7
const MAX_CONCURRENCY = 4

export interface IAppointmentChanges {
  extraFields: {
    [key: string]: any
  }
  notes: string | null
  status: string | null
}

interface ILoadListParams {
  mode: 'single' | 'all'
  resourceId: string
  businessId: string,
  dateFrom: Date
  dateTo: Date
  filters?: TObjectAny
}

interface ILoadAppointmentParams {
  id: string
  businessId: string
}

interface IUpdateAppointmentParams {
  id: string
  businessId: string
  resourceId: string
  changes: IAppointmentChanges
}

interface ISwapDatesParams {
  source: IAppointmentGetAppointment
  target: IAppointmentGetAppointment
}

interface ICancelAppointmentParams {
  id: string
  businessId: string
}

interface IBusinessAppointmentsResult {
  businessId: string
  data: IAppointmentGetAppointment[]
}

class AppointmentsService {
  private logger = loggerNamespace('AppointmentsService')
  private loadListToken = 0

  public async loadList(params: ILoadListParams) {
    const loadToken = ++this.loadListToken
    const isAggregated = params.mode === 'all'
    const normalized = this.normalizeDateRange(params.dateFrom, params.dateTo, isAggregated)
    store.appointments.list = []
    store.appointments.loadErrors = []
    store.appointments.isAggregated = isAggregated
    let resultList: IAppointmentGetAppointment[] = []
    let loadErrors: Array<{ businessId: string, message: string }> = []

    if (isAggregated) {
      const aggregated = await this.loadAggregatedAppointments(normalized.dateFrom, normalized.dateTo, params.filters)
      if (this.loadListToken !== loadToken) {
        return
      }
      resultList = aggregated.list
      loadErrors = aggregated.errors
    } else {

    const resourceId = params.resourceId
    if (!resourceId || resourceId === 'all') {
      this.logger.error('resource not provided for single mode loadList', { params })
      return
    }
    const businessId = store.profile.businessId
    if (!businessId) {
      this.logger.error('business context missing for single mode loadList', { params })
      return
    }

    const filter = {
      ...this.createFilter(normalized.dateFrom, normalized.dateTo),
      ...(params.filters || {}),
      workers: [resourceId]
    }

    const { error, result } = await rpc.call<IGetAppointmentByFilterV2>('appointment.get_appointment_by_filter_v2', {
      business: { id: businessId },
      pageSize: 10000,
      page: 1,
      skipBusinessCancelled: true,
      filter,
      extraFilters: {
        sort: []
      },
      fields: null,
      network: { id: 0 }
    })

      if (this.loadListToken !== loadToken) {
        return
      }

      if (error || !result) {
        this.logger.error('appointment.get_appointment_by_filter_v2', { error })
        resultList = []
      } else {
        resultList = (result.data || []).map(ap => {
          ap.appointment.context = this.buildAppointmentContext(businessId, resourceId)
          return ap
        })
      }
      loadErrors = []
    }

    store.appointments.list = resultList
    store.appointments.isAggregated = isAggregated
    store.appointments.loadErrors = loadErrors
  }

  public async loadAppointment(params: ILoadAppointmentParams) {
    if (!params.id || !params.businessId) return
    await profileService.ensureBusinessContext(params.businessId)
    const { error, result } = await rpc.call<IAppointmentGetAppointment>('appointment.get_appointment', {
      appointment: { id: params.id }
    })
    this.logger.info('appointment.get_appointment', { error, result })
    if (result) {
      result.appointment.context = this.buildAppointmentContext(params.businessId, result.resource?.id)
      store.appointments.appointment = result
    }
  }

  public updateAppointment = async ({ id, businessId, resourceId, changes }: IUpdateAppointmentParams) => {
    if (!store.appointments.appointment) return
    if (!businessId || !resourceId) {
      throw new Error('Missing business context')
    }
    await profileService.ensureBusinessContext(businessId)
    const appointment = JSON.parse(JSON.stringify(store.appointments.appointment))
    for (const [fieldId, value] of Object.entries(changes.extraFields)) {
      const field = appointment.extraFields.find((f: any) => f.fieldID == fieldId)
      if (field) {
        field.value = value
      }
    }
    appointment.notes = changes.notes
    appointment.appointment.status = changes.status
    appointment.business = { id: businessId }
    appointment.resource = {
      ...(appointment.resource ?? {}),
      id: resourceId,
      name: appointment.resource?.name ?? '',
      surname: appointment.resource?.surname ?? '',
    }

    const { error, result } = await rpc.call<IAppointmentUpdateAppointment>('appointment.update_appointment', appointment)
    this.logger.info('appointment.update_appointment', { appointment, error, result })
    if (result) {
      store.appointments.appointment = appointment
    }
  }

  public swapDatesAppointments = async ({ source, target }: ISwapDatesParams) => {
    if (store.appointments.isAggregated) {
      // Drag & drop is not allowed in aggregated view because multiple resources share the same list.
      return { error: t.appointment.selectResourceError }
    }

    const appointments = store.appointments.list.filter(item =>
      item.appointment.id === source.appointment.id || item.appointment.id === target.appointment.id
    )

    if (appointments.length !== 2) return { error: t.appointment.selectResourceError }

    const [firstApp, secondApp] = appointments
    const firstContext = firstApp.appointment.context
    const secondContext = secondApp.appointment.context
    if (!firstContext || !secondContext ||
      firstContext.businessId !== secondContext.businessId ||
      firstContext.resourceId !== secondContext.resourceId
    ) {
      return { error: t.appointment.selectResourceError }
    }

    const copyAppointments = appointments.map(item => JSON.parse(JSON.stringify(item)))
    const { error, result } = await rpc.call<IAppointmentSwapDates>('appointment.swap_dates_appointments', {
      from: copyAppointments[0],
      to: copyAppointments[1],
    })

    this.logger.info('appointment.swap_dates_appointments', { error, result })
    return { error, result }
  }

  public cancelAppointment = async ({ id, businessId }: ICancelAppointmentParams) => {
    if (!id || !businessId) return
    await profileService.ensureBusinessContext(businessId)
    const { error, result } = await rpc.call<IAppointmentGetAppointment>('appointment.cancel_appointment_by_business', {
      appointment: { id }
    })

    this.logger.info('appointment.cancel_appointment_by_business', { error, result })
    return { error, result }
  }

  private normalizeDateRange(dateFrom: Date, dateTo: Date, aggregated: boolean) {
    const from = new Date(dateFrom)
    let to = new Date(dateTo)
    if (to.getTime() < from.getTime()) {
      to = new Date(from.getTime())
    }
    if (aggregated) {
      const maxRangeMs = MAX_AGGREGATED_RANGE_DAYS * 24 * 60 * 60 * 1000
      if (to.getTime() - from.getTime() > maxRangeMs * 2) {
        to = new Date(from.getTime() + maxRangeMs * 2)
      }
    }
    return { dateFrom: from, dateTo: to }
  }

  private createFilter(dateFrom: Date, dateTo: Date) {
    const defaultStatuses = ['CONFIRMED_BY_CLIENT', 'CONFIRMED_BY_BUSINESS', 'CANCELLED_BY_BUSINESS', 'CANCELLED_BY_CLIENT']
    
    // Проверяем опцию скрытия удаленных записей для работников
    const userType = store.profile.profile?.backoffice_operation?.user_type ?? ''
    const isWorker = userType.startsWith('WORKER')
    const backofficeConfig = store.profile.business?.business?.backofficeConfiguration as Record<string, any> | undefined
    const shouldHideCancelled = isWorker && 
      backofficeConfig?.resoureLoginHideCancelledAppointment === true
    
    const status = shouldHideCancelled
      ? defaultStatuses.filter(s => !s.startsWith('CANCELLED_BY_'))
      : defaultStatuses
    
    return {
      status,
      services: [],
      rooms: [],
      start: localDateString(dateFrom),
      end: localDateString(dateTo),
      skipClientNotAppear: true,
      bound_appointments: true
    }
  }

  private async loadAggregatedAppointments(dateFrom: Date, dateTo: Date, filters?: TObjectAny) {
    const resourcesByBusiness = store.profile.resourcesByBusinessId || {}
    const taskEntries = Object.entries(resourcesByBusiness)
      .map(([businessId, resources]) => ({
        businessId,
        workerIds: (resources || []).map(r => r.id)
      }))
      .filter(entry => entry.workerIds.length)

    if (!taskEntries.length) {
      return { list: [], errors: [] as Array<{ businessId: string, message: string }> }
    }

    const tasks = taskEntries.map(entry => () => this.fetchAppointmentsForBusiness(entry.businessId, entry.workerIds, dateFrom, dateTo, filters))
    const settled = await this.limitAllSettled(tasks, MAX_CONCURRENCY)

    const errors: Array<{ businessId: string, message: string }> = []
    const appointments: IAppointmentGetAppointment[] = []

    for (const result of settled) {
      if (result.status === 'fulfilled') {
        const { businessId, data } = result.value
        for (const appointment of data) {
          appointment.appointment.context = this.buildAppointmentContext(businessId, appointment.resource?.id)
          appointments.push(appointment)
        }
        continue
      }
      const reason = result.reason as { businessId?: string, message?: string }
      errors.push({
        businessId: reason?.businessId || 'unknown',
        message: reason?.message || this.stringifyError(reason)
      })
    }

    const list = this.dedupeAndSort(appointments)
    return { list, errors }
  }

  private async fetchAppointmentsForBusiness(businessId: string, workerIds: string[], dateFrom: Date, dateTo: Date, filters?: TObjectAny): Promise<IBusinessAppointmentsResult> {
    try {
      const filter = {
        ...this.createFilter(dateFrom, dateTo),
        ...(filters || {}),
        workers: workerIds
      }

      const { error, result } = await rpc.call<IGetAppointmentByFilterV2>('appointment.get_appointment_by_filter_v2', {
        business: { id: businessId },
        pageSize: 10000,
        page: 1,
        skipBusinessCancelled: true,
        filter,
        extraFilters: {
          sort: []
        },
        fields: null,
        network: { id: 0 }
      })

      if (error || !result) {
        throw {
          businessId,
          message: this.stringifyError(error || 'empty result'),
        }
      }

      return { businessId, data: result.data || [] }
    } catch (err: any) {
      throw {
        businessId,
        message: this.stringifyError(err),
      }
    }
  }

  private async limitAllSettled<T>(tasks: (() => Promise<T>)[], concurrency: number): Promise<PromiseSettledResult<T>[]> {
    if (!tasks.length) return []
    const results: PromiseSettledResult<T>[] = new Array(tasks.length)
    let current = 0
    const workers = Array.from({ length: Math.min(concurrency, tasks.length) }, async () => {
      while (current < tasks.length) {
        const index = current++
        try {
          const value = await tasks[index]()
          results[index] = { status: 'fulfilled', value }
        } catch (reason) {
          results[index] = { status: 'rejected', reason }
        }
      }
    })
    await Promise.all(workers)
    return results
  }

  private dedupeAndSort(appointments: IAppointmentGetAppointment[]) {
    const seen = new Set<string>()
    const uniq: IAppointmentGetAppointment[] = []
    for (const appointment of appointments) {
    const businessId = appointment.appointment.context?.businessId || appointment.business?.id
    const key = businessId ? `${businessId}:${appointment.appointment.id}` : appointment.appointment.id
      if (seen.has(key)) continue
      seen.add(key)
      uniq.push(appointment)
    }
    return uniq.sort((a, b) => {
      const aTime = new Date(a.appointment.start).getTime()
      const bTime = new Date(b.appointment.start).getTime()
      if (aTime !== bTime) {
        return aTime - bTime
      }
      return a.appointment.id.localeCompare(b.appointment.id)
    })
  }

  private buildAppointmentContext(businessId: string, resourceId?: string): IAppointmentContext {
    return {
      businessId,
      resourceId,
      businessName: store.profile.businessesById[businessId]?.name || store.profile.profile?.businessData?.[businessId]?.name,
      resourceName: this.getResourceLabel(resourceId, businessId)
    }
  }

  private getResourceLabel(resourceId?: string, businessId?: string) {
    if (!resourceId) return undefined
    const pool = [
      ...(businessId ? (store.profile.resourcesByBusinessId[businessId] || []) : []),
      ...store.profile.resources
    ]
    const resource = pool.find(r => r.id === resourceId)
    if (!resource) return undefined
    return [resource.name, resource.surname].filter(Boolean).join(' ').trim()
  }

  private stringifyError(value: unknown): string {
    if (!value) return 'Unknown error'
    if (typeof value === 'string') return value
    if (value instanceof Error) return value.message
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value)
      } catch {
        return 'Unknown error'
      }
    }
    return String(value)
  }
}

const appointmentsService = new AppointmentsService()
export { appointmentsService }
